import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { difference } from 'ramda';
import { useTracker } from '@dpdgroupuk/react-event-tracker';
import { makeStyles } from '@mui/styles';

import RightDrawerModal from '../../RightDrawerModal';
import ConsignmentRestrictions from '../../../features/ConsignmentRestrictions';
import WarningBanner from '../../WarningBanner';
import DriverButtons from './components/DriverButtons';
import TransferButtons from './components/TransferButtons';
import SelectDriverRoute from './components/SelectDriverRoute';
import ModalTable from '../../common/Table/ModalTable';
import StaticBanner from '../../StaticBanner';
import ConfirmSnackbar from '../ConfirmSnackbar';
import { WarningIcon } from '../../icons';

import useLocationQueryParams from '../../../hooks/useLocationQueryParams';
import useLoader from '../../../hooks/useLoader';

import {
  DRIVER_REQUEST,
  TRANSFER,
  TRANSFER_HIGH_VALUE_PARCEL,
  UNABLE_TO_TRANSFER_TOTE_PARCELS,
  SELECT_ALL,
  CANCEL,
  UNABLE_TO_PROCESS_REQUEST,
  AFFECTED_REQUESTS,
  TRANSFER_SPLIT_STOP_WARNING,
  CONTINUE_SPLIT_WARNING,
  CONTINUE,
  REJECT_REQUEST_WARNING,
} from '../../../constants/strings';
import {
  DRIVER_REQUESTS_MODAL,
  TRANSFER_REQUESTS_MODAL,
} from '../../../constants/analytics';
import {
  DRIVER_REQUEST_TYPE,
  responseTypes,
} from '../../../enums/requestTypes';
import {
  ACCEPT_DRIVER_REQUEST_PROCESS,
  CONFIRM_DRIVER_REQUEST_PROCESS,
  REJECT_DRIVER_REQUEST_PROCESS,
} from '../../../enums/loaderProcess';

import {
  onAcceptRequest,
  onDeferRequest,
  onRejectRequest,
  onConfirmTransferRequest,
  getAvailableRoutesTransfer,
  onAcceptRequestBatchAction,
  onRejectRequestBatchAction,
} from '../../../store/actions/request';

import { getCurrentUserId } from '../../../store/selectors/user';
import {
  getRoute,
  getRouteDetail,
  getRoutePlannerAddParcelRequests,
  getRouteRequests,
  getRouteStops,
  isRouteStatusLoading,
} from '../../../store/selectors/route';

import { isRouteLocked } from '../TransferTo/utils';
import {
  getAffectedRequests,
  getRemainingConsignmentRequests,
  getRemainingToteRequests,
  getPlannedRequestsForReject,
  getRemainingParcelsFromStops,
  hasAnyPlannedAddRequest,
  isAddParcelRequest,
  isRequestFromRoutePlanner,
  isPKRequest,
} from '../../../models/request';
import {
  isRemoveCollectionRequest,
  isRemoveParcelRequest,
} from '../../../models/routeDetail';
import {
  getRequestType,
  getDriverRequestTableTitle,
  prepareRequestParcels,
  getTableColumnsConfig,
  getPickupTableTitleSubtitle,
  getPickupColumnsConfig,
  getAffectedRequestsTableData,
  getAffectedRequestsTableColumnsConfig,
} from '../../../features/request/model';

import { hasHighValueParcel } from '../../../models/parcel';
import {
  allowToSplitStop,
  isDisableToteTransfer,
} from '../../../models/editStop';
import { arrayToObjectByValue } from '../../../utils/array';
import {
  allowConsignmentSplitting,
  allowToteTransfers,
} from '../../../store/selectors/app';
import useSelectRouteHandler from './useSelectRouteHandler';
import palette from '../../styles/palette';
import StaticBannerWithConfirmActions from '../../StaticBanner/StaticBannerWithConfirmActions';
import { getSelectedStops } from '../../../store/selectors/ui/routeDetail';
import { resetSelectedParcels } from '../../../store/reducers/ui/routeDetail/selectedParcels';

const useStyles = makeStyles(() => ({
  overlay: {
    display: 'flex',
    backgroundColor: palette.typography.black.inactive,
    position: 'absolute',
    zIndex: 9999,
    top: '19px',
    bottom: 0,
    right: 0,
    left: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  },
  messages: {
    minHeight: '40px',
    overflow: 'auto',
  },
  content: {
    position: 'relative',
    flex: 1,
    marginTop: '-40px',
  },
}));

const DriverRequests = ({ open, onClose, stop = {}, activeScreen }) => {
  const styles = useStyles();
  const { requests, stopId } = stop;
  const dispatch = useDispatch();
  const [screen, setActiveScreen] = useState(activeScreen || DRIVER_REQUEST);
  const [showSplitWarningMsg, setShowSplitWarningMsg] = useState(false);
  const tracker = useTracker();
  const [showLoader, hideLoader] = useLoader();
  const query = useLocationQueryParams();
  const { depot, date } = query;

  const [selectedRoute, onSelectRoute, setAvailableRoutes] =
    useSelectRouteHandler();

  const [routes, setRoutes] = useState([]);
  const [warningMessage, setMessage] = useState('');
  const [showSplitConfirmation, setShowSplitConfirmation] = useState(false);
  const [addedRequests, addRemainingRequests] = useState(null);

  const uid = useSelector(getCurrentUserId);
  const route = useSelector(getRouteDetail);
  const { isSubmitting } = useSelector(getRoute);
  const stops = useSelector(getRouteStops);
  const routeAddRequests = useSelector(getRoutePlannerAddParcelRequests);
  const allowDepotConsignmentSplitting = useSelector(allowConsignmentSplitting);
  const allowDepotToteTransfers = useSelector(allowToteTransfers);
  const isLoadingRoute = useSelector(isRouteStatusLoading);
  const selectedStops = useSelector(getSelectedStops);
  const routeRequests = useSelector(getRouteRequests);

  const isDriverScreen = screen === DRIVER_REQUEST;

  useEffect(() => {
    if (!isDriverScreen && isRemoveParcelRequest(requests[0])) {
      if (!stopId) {
        selectedStops.length &&
          selectedStops[0]?.parcel.length > requests.length &&
          setShowSplitWarningMsg(true);
      } else {
        stops?.length &&
          stops.find((stop) => stop.stopId === stopId)?.parcel.length >
            requests.length &&
          setShowSplitWarningMsg(true);
      }
    }
  }, [isDriverScreen]);

  useEffect(() => {
    const fetchRoutes = async () => {
      const availableRoutes = await dispatch(
        getAvailableRoutesTransfer(depot, date, uid)
      );

      setRoutes(availableRoutes);
      setAvailableRoutes(availableRoutes);
    };
    if (!isDriverScreen) {
      fetchRoutes();
    }
  }, [date, depot, isDriverScreen, stop, uid, dispatch]);

  const affectedTableColumnsConfig = getAffectedRequestsTableColumnsConfig();

  const driverRequestType = useMemo(() => getRequestType(stop), [stop]);

  const isRemoveRequest = useMemo(
    () =>
      isRemoveParcelRequest(requests[0]) ||
      isRemoveCollectionRequest(requests[0]),
    [requests]
  );

  const showHighValueParcelNotification = useMemo(
    () =>
      isDriverScreen
        ? hasAnyPlannedAddRequest(requests) && hasHighValueParcel(requests)
        : hasHighValueParcel(requests),
    [isDriverScreen, requests]
  );

  const allowedSplitStop = useMemo(() => {
    const remainingParcels = getRemainingParcelsFromStops(stops, requests);

    return (
      allowDepotConsignmentSplitting ||
      (isDriverScreen && !isPKRequest(requests[0])) ||
      allowToSplitStop(requests, remainingParcels)
    );
  }, [isDriverScreen, requests, stops, allowDepotConsignmentSplitting]);

  const disableToteTransfer = useMemo(
    () =>
      !allowDepotToteTransfers &&
      (!isDriverScreen || isPKRequest(requests[0])) &&
      isDisableToteTransfer(requests, { stop: stops }),
    [stops, requests, isDriverScreen, allowDepotToteTransfers]
  );

  const affectedRequests = useMemo(() => {
    const conditions = [];
    if (!allowDepotConsignmentSplitting) {
      conditions.push(getRemainingConsignmentRequests);
    }
    if (!allowDepotToteTransfers) {
      conditions.push(getRemainingToteRequests);
    }
    return getAffectedRequests(routeAddRequests, requests, conditions);
  }, [
    allowDepotConsignmentSplitting,
    allowDepotToteTransfers,
    routeAddRequests,
    requests,
  ]);

  const forbiddenProcessRequest = useMemo(
    () =>
      isRequestFromRoutePlanner(requests[0]) &&
      isAddParcelRequest(requests[0]) &&
      affectedRequests.length !== requests.length &&
      !addedRequests,
    [addedRequests, affectedRequests, requests]
  );

  const getTitle = useCallback(
    () => (isDriverScreen ? DRIVER_REQUEST : TRANSFER).toUpperCase(),
    [isDriverScreen]
  );

  const getRequestsData = useCallback(
    () => getAffectedRequestsTableData(addedRequests, route),
    [addedRequests, route]
  );

  const getTableColumns = useCallback(
    () => getTableColumnsConfig(stop),
    [stop]
  );
  const getParcelData = useCallback(
    () => prepareRequestParcels(stop, route),
    [route, stop]
  );

  const getTableTitle = useCallback(
    () => getDriverRequestTableTitle(stop),
    [stop]
  );

  const getPickupColumns = useCallback(
    () => getPickupColumnsConfig(stop),
    [stop]
  );
  const getPickupTableTitle = useCallback(
    () => getPickupTableTitleSubtitle(stop).title,
    [stop]
  );
  const getPickupTableSubtitle = useCallback(
    () => getPickupTableTitleSubtitle(stop).subtitle,
    [stop]
  );

  const selectDriverRoute = useCallback(
    async (value) => {
      onSelectRoute(value);
    },
    [onSelectRoute]
  );

  const onClickClose = useCallback(() => {
    tracker.logEvent(
      isDriverScreen
        ? DRIVER_REQUESTS_MODAL.CLICK_CLOSE
        : TRANSFER_REQUESTS_MODAL.CLICK_CLOSE
    );
    onClose();
  }, [isDriverScreen, onClose, tracker]);

  const onDefer = useCallback(() => {
    tracker.logEvent(DRIVER_REQUESTS_MODAL.CLICK_DEFER);
    dispatch(onDeferRequest(requests));
    onClose();
  }, [onClose, requests, dispatch, tracker]);

  const onReject = useCallback(async () => {
    tracker.logEvent(DRIVER_REQUESTS_MODAL.CLICK_REJECT);
    if (forbiddenProcessRequest) {
      return setMessage(UNABLE_TO_PROCESS_REQUEST);
    }

    showLoader(REJECT_DRIVER_REQUEST_PROCESS);

    const requestsForReject = getPlannedRequestsForReject(requests);
    let isSuccess = false;

    if (requestsForReject.length) {
      isSuccess = addedRequests
        ? await dispatch(onRejectRequestBatchAction(affectedRequests))
        : await dispatch(onRejectRequest(requestsForReject));
    }

    if (requestsForReject.length !== requests.length) {
      setActiveScreen(TRANSFER);
    } else if (isSuccess) {
      dispatch(resetSelectedParcels());
      onClose();
    }

    hideLoader(REJECT_DRIVER_REQUEST_PROCESS);
  }, [
    tracker,
    forbiddenProcessRequest,
    showLoader,
    requests,
    hideLoader,
    addedRequests,
    dispatch,
    affectedRequests,
    onClose,
  ]);

  const onAccept = useCallback(async () => {
    tracker.logEvent(DRIVER_REQUESTS_MODAL.CLICK_ACCEPT);
    try {
      showLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
      if (isRemoveRequest) {
        setActiveScreen(TRANSFER);
      } else {
        if (forbiddenProcessRequest) {
          hideLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
          return setMessage(UNABLE_TO_PROCESS_REQUEST);
        }
        let isSuccess;
        if (addedRequests) {
          isSuccess = await dispatch(
            onAcceptRequestBatchAction(affectedRequests)
          );
        } else {
          isSuccess = await dispatch(onAcceptRequest(stop));
        }
        isSuccess && onClose();
      }
    } finally {
      hideLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
    }
  }, [
    tracker,
    showLoader,
    isRemoveRequest,
    forbiddenProcessRequest,
    affectedRequests,
    dispatch,
    addedRequests,
    stop,
    onClose,
    hideLoader,
  ]);

  const onConfirm = useCallback(async () => {
    tracker.logEvent(TRANSFER_REQUESTS_MODAL.CLICK_CONFIRM);
    showLoader(CONFIRM_DRIVER_REQUEST_PROCESS);

    const generatedRequests = await dispatch(
      onConfirmTransferRequest(stop, selectedRoute)
    );
    if (generatedRequests) {
      dispatch(resetSelectedParcels());
      onClose();
    }
    hideLoader(CONFIRM_DRIVER_REQUEST_PROCESS);
  }, [onClose, stop, selectedRoute, showLoader, hideLoader, dispatch, tracker]);

  const showAffectedTable = useMemo(
    () => addedRequests && !!Object.values(addedRequests).length,
    [addedRequests]
  );

  const handleReject = useCallback(() => {
    if (
      isRequestFromRoutePlanner(requests[0]) &&
      isAddParcelRequest(requests[0]) &&
      isLoadingRoute
    ) {
      const stopNumber = !stopId
        ? selectedStops[0].stopNumber
        : stop.stopNumber;
      const notRejectedRequests =
        routeRequests?.length &&
        routeRequests.reduce((acc, request) => {
          if (
            request.stopNumber === stopNumber &&
            isAddParcelRequest(request) &&
            (!request.response ||
              request.response?.responseType !== responseTypes.REJECT)
          ) {
            acc.push(request);
          }
          return acc;
        }, []);

      if (requests.length !== notRejectedRequests.length) {
        return setShowSplitConfirmation(true);
      }
    }
    onReject();
  }, [
    isLoadingRoute,
    onReject,
    requests,
    routeRequests,
    selectedStops,
    stop,
    stopId,
  ]);

  const onConfirmRejectWarning = useCallback(() => {
    setShowSplitConfirmation(false);
    onReject();
  }, [onReject]);

  const getActions = useCallback(
    () =>
      isDriverScreen ? (
        <DriverButtons
          onDefer={onDefer}
          onReject={handleReject}
          onAccept={onAccept}
          isDisabledAccept={
            isSubmitting || !allowedSplitStop || disableToteTransfer
          }
          isDisabledDefer={showAffectedTable}
        />
      ) : (
        <TransferButtons
          onClickConfirm={onConfirm}
          disabled={
            !selectedRoute ||
            isRouteLocked(routes, selectedRoute) ||
            !allowedSplitStop ||
            disableToteTransfer
          }
        />
      ),
    [
      isDriverScreen,
      onDefer,
      handleReject,
      onAccept,
      isSubmitting,
      allowedSplitStop,
      disableToteTransfer,
      showAffectedTable,
      onConfirm,
      selectedRoute,
      routes,
    ]
  );

  const onAddRemainingRequests = useCallback(() => {
    addRemainingRequests(
      arrayToObjectByValue(difference(affectedRequests, requests), 'stopNumber')
    );
    setMessage('');
  }, [affectedRequests, requests]);

  const onCloseWarningMsg = useCallback(() => setMessage(''), []);

  return (
    <RightDrawerModal
      open={open}
      onClose={onClickClose}
      title={getTitle()}
      loadId={
        isDriverScreen
          ? DRIVER_REQUESTS_MODAL.LOAD
          : TRANSFER_REQUESTS_MODAL.LOAD
      }
      actions={getActions()}
    >
      <div className={styles.container}>
        <div className={styles.messages}>
          {!allowedSplitStop && (
            <ConsignmentRestrictions withActionButton={false} />
          )}
          {allowedSplitStop && disableToteTransfer && (
            <WarningBanner message={UNABLE_TO_TRANSFER_TOTE_PARCELS} />
          )}
          {showHighValueParcelNotification && (
            <StaticBanner message={TRANSFER_HIGH_VALUE_PARCEL} />
          )}
          {showSplitWarningMsg && (
            <StaticBannerWithConfirmActions
              onContinue={() => setShowSplitWarningMsg(false)}
              onCancel={onClickClose}
              message={`${TRANSFER_SPLIT_STOP_WARNING} ${CONTINUE_SPLIT_WARNING}`}
            />
          )}
          <ConfirmSnackbar
            confirmText={CONTINUE}
            declineText={CANCEL}
            open={!!showSplitConfirmation}
            onConfirm={onConfirmRejectWarning}
            onClose={() => setShowSplitConfirmation(false)}
            messageIcon={<WarningIcon />}
          >
            {`${REJECT_REQUEST_WARNING} ${CONTINUE_SPLIT_WARNING}`}
          </ConfirmSnackbar>
          <ConfirmSnackbar
            confirmText={SELECT_ALL}
            declineText={CANCEL}
            open={!!warningMessage}
            onConfirm={onAddRemainingRequests}
            onClose={onCloseWarningMsg}
            messageIcon={<WarningIcon />}
          >
            {warningMessage}
          </ConfirmSnackbar>
        </div>
        <div className={styles.content}>
          {showSplitWarningMsg && <div className={styles.overlay} />}
          <ModalTable
            title={getTableTitle()}
            columns={getTableColumns()}
            data={getParcelData()}
          />
          {showAffectedTable && (
            <ModalTable
              title={AFFECTED_REQUESTS}
              columns={affectedTableColumnsConfig}
              data={getRequestsData()}
            />
          )}
          {allowedSplitStop && !disableToteTransfer && (
            <>
              {driverRequestType === DRIVER_REQUEST_TYPE.PICKUP && (
                <ModalTable
                  title={getPickupTableTitle()}
                  subTitle={getPickupTableSubtitle()}
                  columns={getPickupColumns()}
                  data={getParcelData()}
                />
              )}
              {!isDriverScreen && (
                <SelectDriverRoute
                  options={routes}
                  onChange={selectDriverRoute}
                  value={selectedRoute}
                  disabled={showSplitWarningMsg}
                />
              )}
            </>
          )}
        </div>
      </div>
    </RightDrawerModal>
  );
};

export default DriverRequests;
